import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customers } from '../services/api';
import LayoutV2 from '../components/LayoutV2';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function getRecentSevenDaysRange() {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - 30);
  const start = startDate.toISOString().slice(0, 10);
  return { fromDate: start, toDate: end };
}

function getStatusVariant(statusCode) {
  const map = {
    closed_deal: 'success',
    rejected: 'danger',
    applied: 'blue',
    kyc_submitted: 'primary',
    registered: 'gray',
    invalid: 'danger',
    unregistered: 'warning'
  };
  return map[statusCode] || 'gray';
}

export default function CustomersV2() {
  const { isAdmin, isOperations } = useAuth();
  const isAdminOrOps = isAdmin || isOperations;
  const location = useLocation();
  const restoredState = location.state?.customersState || (() => {
    try {
      return JSON.parse(sessionStorage.getItem('customers_v2_state') || 'null');
    } catch {
      return null;
    }
  })();

  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState(() => {
    if (restoredState?.searchParams) {
      return restoredState.searchParams;
    }
    if (isAdmin) {
      return { mobile: '', name: '', fromDate: '', toDate: '' };
    }
    return { mobile: '', name: '', ...getRecentSevenDaysRange() };
  });
  const [pagination, setPagination] = useState({
    page: restoredState?.page || 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    const loadCustomers = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...searchParams
        };

        Object.keys(params).forEach((key) => {
          if (!params[key]) delete params[key];
        });

        const res = await customers.list(params);
        setCustomersList(res.data.customers || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.total || 0,
          totalPages: res.data.totalPages || 0
        }));
      } catch (err) {
        console.error('Failed to load customers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, [pagination.page, pagination.limit, searchParams]);

  useEffect(() => {
    sessionStorage.setItem('customers_v2_state', JSON.stringify({
      searchParams,
      page: pagination.page
    }));
  }, [searchParams, pagination.page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const title = isAdmin ? 'All Customers' : 'My Customers';
  const description = isAdmin
    ? 'Use filters to inspect the shared customer pool across promoters and drill into detail records.'
    : 'Monitor your recent customer registrations, approvals, and outcomes with quick mobile-friendly filters.';

  return (
    <LayoutV2>
      <PageHeader
        eyebrow="Customer Pipeline"
        title={title}
        description={description}
        actions={<Badge variant="primary">{isAdmin ? 'Admin scope' : 'Personal scope'}</Badge>}
      />

      <div className="mt-4 space-y-6">
        <SectionBlock
          eyebrow="Filters"
          title="Search customers"
          description="Keep filters compact on mobile and fast to scan on desktop."
          actions={
            <Button type="submit" form="customers-search-form" variant="primary">
              Search
            </Button>
          }
        >
          <form id="customers-search-form" onSubmit={handleSearch} className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div>
              <label className="label">Mobile Phone</label>
              <input
                type="text"
                name="mobile"
                value={searchParams.mobile}
                onChange={handleInputChange}
                placeholder="Search by phone"
                className="input"
              />
            </div>
            <div>
              <label className="label">Customer Name</label>
              <input
                type="text"
                name="name"
                value={searchParams.name}
                onChange={handleInputChange}
                placeholder="Search by name"
                className="input"
              />
            </div>
            <div>
              <label className="label">From Date</label>
              <input
                type="date"
                name="fromDate"
                value={searchParams.fromDate}
                onChange={handleInputChange}
                className="input"
              />
            </div>
            <div>
              <label className="label">To Date</label>
              <input
                type="date"
                name="toDate"
                value={searchParams.toDate}
                onChange={handleInputChange}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-center"
                onClick={() => {
                  setSearchParams(isAdmin ? { mobile: '', name: '', fromDate: '', toDate: '' } : { mobile: '', name: '', ...getRecentSevenDaysRange() });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                Reset
              </Button>
            </div>
          </form>
        </SectionBlock>

        <SectionBlock
          eyebrow="Results"
          title="Customer list"
          description="Desktop users get a high-density table; mobile users get readable customer cards without horizontal scanning."
        >
          {loading ? (
            <Loading text="Loading customers..." />
          ) : customersList.length === 0 ? (
            <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
              No customers found for the current filters.
            </div>
          ) : (
            <>
              <div className="hidden lg:block table-container">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Mobile</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customersList.map((customer) => (
                        <tr key={customer.id}>
                          <td className="font-mono">{customer.mobile}</td>
                          <td>{customer.user_name || 'N/A'}</td>
                          <td>
                            <Badge variant={getStatusVariant(customer.customerStatus)}>
                              {customer.customerStatusLabel || '--'}
                            </Badge>
                          </td>
                          <td>
                            <Link
                              to={`/customers/${customer.mobile}`}
                              state={{
                                customersState: {
                                  searchParams,
                                  page: pagination.page
                                }
                              }}
                              className="text-sm font-medium text-blue-200 hover:text-blue-100"
                            >
                              View details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:hidden">
                {customersList.map((customer) => (
                  <div key={customer.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-100">{customer.user_name || 'N/A'}</p>
                        <p className="mt-1 font-mono text-sm text-slate-400">{customer.mobile}</p>
                      </div>
                      <Badge variant={getStatusVariant(customer.customerStatus)}>
                        {customer.customerStatusLabel || '--'}
                      </Badge>
                    </div>
                    <div className="mt-4">
                      <Link
                        to={`/customers/${customer.mobile}`}
                        state={{
                          customersState: {
                            searchParams,
                            page: pagination.page
                          }
                        }}
                        className="text-sm font-medium text-blue-200 hover:text-blue-100"
                      >
                        View details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 ? (
                <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm text-slate-400">
                    Page {pagination.page} of {pagination.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </SectionBlock>
      </div>
    </LayoutV2>
  );
}
